import { Copy, RefreshCw } from 'lucide-react';
import { useProviderDiagnose, type DiagnoseReport, type DiagnoseRow, type DiagnoseVerdict } from '@/api/hooks/integrations';
import { Badge, type Tone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { formatAgo } from '@/lib/format';
import { toast } from '@/stores/toast';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  СВЕРКА С FROTCOM.
 *
 *  Клиентът: „стойностите от Frotcom са грешни, бусовете не са на точното
 *  място". Тук се вижда ЧИЯ е грешката, бус по бус: какво казва Frotcom в
 *  момента срещу какво е записано при нас — разстояние, закъснение, присъда.
 *  „Копирай доклад" дава готов текст за поддръжката на Frotcom.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const VERDICT: Record<DiagnoseVerdict, { tone: Tone; label: string; who: 'ok' | 'ours' | 'frotcom' | 'setup' }> = {
  ok: { tone: 'ok', label: 'Съвпада', who: 'ok' },
  ours_stale: { tone: 'danger', label: 'При нас е старо', who: 'ours' },
  mismatch: { tone: 'danger', label: 'Различно място', who: 'ours' },
  frotcom_inconsistent: { tone: 'warn', label: 'Frotcom си противоречи', who: 'frotcom' },
  no_signal: { tone: 'warn', label: 'Устройството мълчи', who: 'frotcom' },
  no_gps: { tone: 'warn', label: 'Няма GPS при Frotcom', who: 'frotcom' },
  privacy: { tone: 'neutral', label: 'Режим „поверителност“', who: 'frotcom' },
  not_linked: { tone: 'warn', label: 'Не е свързан с наш бус', who: 'setup' },
};

const fmtCoord = (lat: number | null | undefined, lon: number | null | undefined): string =>
  lat == null || lon == null ? '—' : `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
const mapsLink = (lat: number | null | undefined, lon: number | null | undefined): string | null =>
  lat == null || lon == null ? null : `https://www.google.com/maps?q=${lat},${lon}`;
const fmtTime = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString('bg-BG', { timeZone: 'Europe/Sofia', dateStyle: 'short', timeStyle: 'medium' }) : '—';
const fmtDistance = (m: number | null): string => (m == null ? '—' : m >= 1000 ? `${(m / 1000).toFixed(1)} км` : `${m} м`);

/** Текстът за поддръжката на Frotcom — само това, което е тяхно. */
function frotcomReport(report: DiagnoseReport): string {
  const lines = [
    `Сверка Frotcom API — ${fmtTime(report.checkedAt)} (време София)`,
    `Профил: ${report.provider}`,
    `GET /v2/vehicles връща ${report.frotcomVehicles} превозни средства за нашия API потребител.`,
    '',
  ];
  for (const row of report.vehicles) {
    const f = row.frotcom;
    lines.push(
      `• ${row.frotcomPlate ?? '—'} (Frotcom id ${row.externalId}${row.frotcomVehicleId ? `, vehicleId ${row.frotcomVehicleId}` : ''}) — ${VERDICT[row.verdict].label}`,
      `  /v2/vehicles: ${fmtCoord(f.listLat, f.listLon)}, lastGps ${f.listAt ?? '—'}, lastCommunication ${f.lastCommunication ?? '—'}, noGps ${f.noGpsPct ?? '—'}%`,
      `  /v2/vehicles/locations (15 мин): ${f.feedPoints} точки${f.source === 'feed' ? `, последна ${fmtCoord(f.lat, f.lon)} в ${f.at}` : ''}`,
      `  lastVehiclePosition: ${fmtCoord(f.lastPosition?.lat, f.lastPosition?.lon)}`,
      `  одометър: GPS ${f.odometerGps ?? '—'}, CAN ${f.odometerCanbus ?? '—'}, useCanbusMileage ${f.useCanbusMileage ?? '—'}`,
    );
    if (row.listVsFeedM != null && row.listVsFeedM > 500) {
      lines.push(`  ВНИМАНИЕ: /v2/vehicles и /v2/vehicles/locations се разминават с ${fmtDistance(row.listVsFeedM)}.`);
    }
  }
  return lines.join('\n');
}

export function FrotcomDiagnoseDialog({
  providerId,
  open,
  onClose,
}: {
  providerId: string;
  open: boolean;
  onClose: () => void;
}) {
  const query = useProviderDiagnose(providerId, open);
  const report = query.data;

  const counts = (report?.vehicles ?? []).reduce(
    (acc, row) => {
      acc[VERDICT[row.verdict].who] += 1;
      return acc;
    },
    { ok: 0, ours: 0, frotcom: 0, setup: 0 },
  );

  const copy = async (): Promise<void> => {
    if (!report) return;
    try {
      await navigator.clipboard.writeText(frotcomReport(report));
      toast.ok('Докладът е копиран — поставете го в писмото до Frotcom.');
    } catch {
      toast.error('Копирането не успя.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width="xl"
      title="Сверка с Frotcom"
      description="Какво казва Frotcom в момента за всеки бус срещу това, което е записано при нас."
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button icon={<Copy className="size-4" />} onClick={() => void copy()} disabled={!report}>
            Копирай доклад за Frotcom
          </Button>
          <Button
            variant="primary"
            icon={<RefreshCw className="size-4" />}
            loading={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Провери отново
          </Button>
        </div>
      }
    >
      {query.isError ? (
        <p className="text-danger">{query.error instanceof Error ? query.error.message : 'Сверката не успя.'}</p>
      ) : !report ? (
        <p className="text-ink-dim">Питам Frotcom…</p>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-2 sm:grid-cols-4">
            <Summary tone="ok" value={counts.ok} label="съвпадат" />
            <Summary tone="danger" value={counts.ours} label="грешка при нас" />
            <Summary tone="warn" value={counts.frotcom} label="данни от Frotcom" />
            <Summary tone="neutral" value={counts.setup} label="не са свързани" />
          </div>

          <p className="text-sm">
            Frotcom показва на нашия API потребител <b>{report.frotcomVehicles}</b>{' '}
            {report.frotcomVehicles === 1 ? 'превозно средство' : 'превозни средства'}. Ако в уеб приложението на Frotcom
            са повече, API потребителят няма права за останалите — това се отключва при Frotcom.
          </p>
          <p className="text-xs text-ink-dim">
            Последно изтегляне {report.lastSyncAt ? formatAgo(report.lastSyncAt) : '—'}
            {report.lastError ? <span className="text-danger"> · последна грешка: {report.lastError}</span> : null}
            {report.ingestion.pausedProviders > 0 ? (
              <span className="text-warn"> · изтеглянето е на пауза след грешка, нов опит до 10 мин.</span>
            ) : null}
          </p>

          <div className="space-y-3">
            {report.vehicles.map((row) => (
              <VehicleRow key={row.externalId} row={row} />
            ))}
          </div>

          {report.oursWithoutFrotcom.length > 0 ? (
            <p className="text-sm text-warn">
              Наши бусове без връзка с Frotcom: {report.oursWithoutFrotcom.map((v) => `${v.fleetNo} (${v.plate})`).join(', ')}.
            </p>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}

function Summary({ tone, value, label }: { tone: Tone; value: number; label: string }) {
  return (
    <div className="kmdx-card px-4 py-3">
      <div className="font-display text-2xl">{value}</div>
      <Badge tone={tone}>{label}</Badge>
    </div>
  );
}

function VehicleRow({ row }: { row: DiagnoseRow }) {
  const v = VERDICT[row.verdict];
  const f = row.frotcom;
  const o = row.ours;
  const theirLink = mapsLink(f.lat, f.lon);
  const ourLink = mapsLink(o?.lat, o?.lon);
  return (
    <div className="kmdx-card p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="font-display text-base">{row.fleetNo ? `Бус ${row.fleetNo}` : 'Без наш бус'}</span>
        <span className="font-mono text-sm text-ink-dim">{row.plate ?? row.frotcomPlate ?? '—'}</span>
        <Badge tone={v.tone}>{v.label}</Badge>
        <span className="ml-auto font-mono text-xs text-ink-faint">Frotcom id {row.externalId}</span>
      </div>
      <div className="grid gap-3 text-sm sm:grid-cols-2">
        <div>
          <div className="mb-1 text-xs uppercase tracking-wider text-ink-faint">
            Frotcom сега ({f.source === 'feed' ? `поток, ${f.feedPoints} точки за 15 мин` : 'списък'})
          </div>
          <div className="font-mono">
            {theirLink ? (
              <a className="underline" href={theirLink} target="_blank" rel="noreferrer">
                {fmtCoord(f.lat, f.lon)}
              </a>
            ) : (
              '—'
            )}
          </div>
          <div className="text-ink-dim">
            {fmtTime(f.at)} · {f.speed != null ? `${Math.round(f.speed)} км/ч` : '—'} · последна връзка {fmtTime(f.lastCommunication)}
          </div>
          <div className="text-ink-dim">
            Одометър: CAN {f.odometerCanbus != null ? Math.round(f.odometerCanbus) : '—'} · GPS{' '}
            {f.odometerGps != null ? Math.round(f.odometerGps) : '—'} км
          </div>
        </div>
        <div>
          <div className="mb-1 text-xs uppercase tracking-wider text-ink-faint">При нас</div>
          {o ? (
            <>
              <div className="font-mono">
                {ourLink ? (
                  <a className="underline" href={ourLink} target="_blank" rel="noreferrer">
                    {fmtCoord(o.lat, o.lon)}
                  </a>
                ) : (
                  '—'
                )}
              </div>
              <div className="text-ink-dim">
                {fmtTime(o.at)} · {o.speed != null ? `${o.speed} км/ч` : '—'} · {o.motionState ?? '—'}
              </div>
              <div className="text-ink-dim">Одометър: {o.odometerKm ?? '—'} км</div>
            </>
          ) : (
            <div className="text-ink-dim">Няма наш бус с този Frotcom id.</div>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-ink-dim">
        <span>Разстояние наше ↔ Frotcom: {fmtDistance(row.distanceM)}</span>
        <span>Закъснение: {row.lagSec == null ? '—' : `${row.lagSec} с`}</span>
        {row.listVsFeedM != null ? <span>Списък ↔ поток при Frotcom: {fmtDistance(row.listVsFeedM)}</span> : null}
      </div>
    </div>
  );
}
